import { apiService } from "@/services";
import { useQuery } from "@tanstack/react-query";
import { SeoInsight, SeoInsightLatestResponse, SeoInsightListItem, isSeoInsightPending } from "@/types";
import { DEFAULT_STALE_TIME } from "@/constants";

const LATEST_POLL_INTERVAL_MS = 7000;

export const useGetSeoInsightsList = (
  productId?: number,
  dateFrom?: string,
  dateTo?: string,
) => {
  return useQuery({
    queryKey: ["seo-insights", "list", productId, dateFrom, dateTo],
    queryFn: () => {
      const params: Record<string, string | number> = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      return apiService.get<SeoInsightListItem[], Record<string, string | number>>(
        `/products/hero/${productId}/seo-insights`,
        params,
      );
    },
    enabled: !!productId,
    staleTime: DEFAULT_STALE_TIME,
  });
};

export const useGetLatestSeoInsight = (productId?: number) => {
  return useQuery<SeoInsightLatestResponse>({
    queryKey: ["seo-insights", "latest", productId],
    queryFn: () =>
      apiService.get<SeoInsightLatestResponse, undefined>(
        `/products/hero/${productId}/seo-insights/latest`,
        undefined,
      ),
    enabled: !!productId,
    staleTime: DEFAULT_STALE_TIME,
    retry: false,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return isSeoInsightPending(data) ? LATEST_POLL_INTERVAL_MS : false;
    },
  });
};

export const useGetSeoInsight = (
  productId?: number,
  insightId?: number | null,
) => {
  return useQuery({
    queryKey: ["seo-insights", "detail", productId, insightId],
    queryFn: () =>
      apiService.get<SeoInsight, undefined>(
        `/products/hero/${productId}/seo-insights/${insightId}`,
        undefined,
      ),
    enabled: !!productId && !!insightId,
    staleTime: DEFAULT_STALE_TIME,
  });
};
